import * as THREE from 'three';
import type { Brand } from '../brand/types';
import { ballOnToZ } from '../sim/field';
import type { Tunables } from '../sim/tunables';
import { drawFieldCanvas, grassDetailCanvas } from './fieldTexture';

export interface FieldView {
  group: THREE.Group;
  /** Moves the line of scrimmage and the first-down line. */
  setMarkers(ballOn: number, toGo: number): void;
}

export function createField(brand: Brand, t: Tunables, logo: HTMLImageElement | null, maxAnisotropy: number): FieldView {
  const f = t.field;
  const group = new THREE.Group();

  const texture = new THREE.CanvasTexture(drawFieldCanvas(brand, t, logo));
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.min(maxAnisotropy, t.render.anisotropy);
  // A small grain texture tiles over the painted field so it still looks like grass up close.
  const detail = new THREE.CanvasTexture(grassDetailCanvas(128));
  detail.wrapS = detail.wrapT = THREE.RepeatWrapping;
  detail.anisotropy = texture.anisotropy;
  const fieldMat = new THREE.MeshLambertMaterial({ map: texture });
  fieldMat.onBeforeCompile = (shader) => {
    shader.uniforms['uDetail'] = { value: detail };
    shader.uniforms['uDetailRepeat'] = { value: new THREE.Vector2(f.widthYards / f.detailRepeatYards, f.lengthYards / f.detailRepeatYards) };
    shader.uniforms['uDetailStrength'] = { value: f.detailStrength };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform vec2 uDetailRepeat;\nvarying vec2 vDetailUv;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvDetailUv = uv * uDetailRepeat;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D uDetail;\nuniform float uDetailStrength;\nvarying vec2 vDetailUv;')
      .replace('#include <map_fragment>', '#include <map_fragment>\ndiffuseColor.rgb *= 1.0 + (texture2D(uDetail, vDetailUv).r - 0.5) * 2.0 * uDetailStrength;');
  };
  const field = new THREE.Mesh(new THREE.PlaneGeometry(f.widthYards, f.lengthYards), fieldMat);
  field.rotation.x = -Math.PI / 2;
  group.add(field);

  const surroundW = f.widthYards + 2 * f.surroundYards;
  const surroundL = f.lengthYards + 2 * f.surroundYards;
  const surround = new THREE.Mesh(
    new THREE.PlaneGeometry(surroundW, surroundL),
    new THREE.MeshLambertMaterial({ color: t.look.surround }),
  );
  surround.rotation.x = -Math.PI / 2;
  surround.position.y = -f.markerLineLiftYards;
  group.add(surround);

  const halfL = f.lengthYards / 2;
  group.add(createGoalpost(t, -halfL));
  group.add(createGoalpost(t, halfL));

  const markerGeo = new THREE.PlaneGeometry(f.widthYards, f.markerLineWidthYards);
  const scrimmage = new THREE.Mesh(markerGeo, new THREE.MeshBasicMaterial({ color: brand.colors.scrimmage }));
  const firstDown = new THREE.Mesh(markerGeo, new THREE.MeshBasicMaterial({ color: brand.colors.firstDown }));
  for (const m of [scrimmage, firstDown]) {
    m.rotation.x = -Math.PI / 2;
    m.position.y = f.markerLineLiftYards;
    group.add(m);
  }

  return {
    group,
    setMarkers(ballOn, toGo) {
      scrimmage.position.z = ballOnToZ(ballOn, t);
      firstDown.position.z = ballOnToZ(ballOn + toGo, t);
    },
  };
}

function createGoalpost(t: Tunables, z: number): THREE.Group {
  const g = t.goalpost;
  const group = new THREE.Group();
  const mat = new THREE.MeshLambertMaterial({ color: t.look.goalpost });
  const r = g.radiusYards;
  const base = new THREE.Mesh(new THREE.CylinderGeometry(r, r, g.baseHeightYards, g.radialSegments), mat);
  base.position.set(0, g.baseHeightYards / 2, 0);
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(r, r, g.uprightGapYards, g.radialSegments), mat);
  bar.rotation.z = Math.PI / 2;
  bar.position.set(0, g.baseHeightYards, 0);
  group.add(base, bar);
  for (const side of [-1, 1]) {
    const up = new THREE.Mesh(new THREE.CylinderGeometry(r, r, g.uprightHeightYards, g.radialSegments), mat);
    up.position.set((side * g.uprightGapYards) / 2, g.baseHeightYards + g.uprightHeightYards / 2, 0);
    group.add(up);
  }
  group.position.z = z;
  return group;
}
